import {
  PREVIOUS_SOAP_NOTE_SOCIAL_UPDATE_LABEL,
  type AppointmentPreviousSoapNoteSignedAtBasis,
} from "../../../shared/api-types";
import { SOAP_SECTION_LABELS, type SoapSectionLabel } from "./soap-note-sections";

/**
 * Pure logic for the Previous SOAP notes drawer: navigation between the
 * overview and the reader, the pager, the jump bar, and the scroll-spy rule.
 * No clinical text passes through here, only indexes, labels, and offsets.
 */

export interface DrawerNav {
  mode: "overview" | "reader";
  /** The note shown in the reader; meaningful when `mode` is "reader". */
  index: number;
  /** The overview item the reader was opened from, for focus on the way back. */
  originIndex: number | null;
  /** The overview's scroll offset when the reader opened, restored on the way back. */
  overviewScrollTop: number;
}

export type DrawerNavAction =
  | { type: "open_note"; index: number; overviewScrollTop: number }
  | { type: "newer" }
  | { type: "older"; total: number }
  | { type: "back_to_overview" }
  | { type: "reset" }
  /** The drawer opened or closed: either way it comes back on the overview. */
  | { type: "open_changed" };

export const INITIAL_DRAWER_NAV: DrawerNav = {
  mode: "overview",
  index: 0,
  originIndex: null,
  overviewScrollTop: 0,
};

export function drawerNavReducer(state: DrawerNav, action: DrawerNavAction): DrawerNav {
  switch (action.type) {
    case "open_note":
      return {
        mode: "reader",
        index: Math.max(0, action.index),
        originIndex: action.index,
        overviewScrollTop: action.overviewScrollTop,
      };
    case "newer":
      return { ...state, index: Math.max(0, state.index - 1) };
    case "older":
      return {
        ...state,
        index: Math.max(0, Math.min(action.total - 1, state.index + 1)),
      };
    case "back_to_overview":
      return { ...state, mode: "overview" };
    case "reset":
    case "open_changed":
      return INITIAL_DRAWER_NAV;
  }
}

export interface PagerState {
  label: string;
  newerDisabled: boolean;
  olderDisabled: boolean;
}

export function pagerState(index: number, total: number): PagerState {
  return {
    label: `${index + 1} of ${total}`,
    newerDisabled: index <= 0,
    olderDisabled: index >= total - 1,
  };
}

export interface JumpBarItem {
  label: SoapSectionLabel;
  key: string;
}

/** SOAP sections the note has, in S, O, A, P order; none below two. */
export function jumpBarItems(
  sections: readonly { key: string; soap: SoapSectionLabel | null; target: boolean }[]
): JumpBarItem[] {
  const items: JumpBarItem[] = [];

  for (const label of SOAP_SECTION_LABELS) {
    const section = sections.find(
      (candidate) => candidate.target && candidate.soap === label
    );
    if (section) items.push({ label, key: section.key });
  }

  return items.length < 2 ? [] : items;
}

/**
 * The SOAP label of the section whose range holds the top edge of the note:
 * the last heading at or above it. A heading outside the four, or none yet,
 * clears the highlight, and the bottom of the note never forces one.
 */
export function activeSoapSection(
  headings: readonly { top: number; soap: SoapSectionLabel | null }[],
  viewportTop: number
): SoapSectionLabel | null {
  let active: SoapSectionLabel | null = null;

  for (const heading of headings) {
    if (heading.top > viewportTop) break;

    active = heading.soap;
  }

  return active;
}

/** Splits the "Social update: " label off a server preview so it can be styled. */
export function splitPreviewLabel(preview: string): {
  label: string | null;
  text: string;
} {
  if (!preview.startsWith(PREVIOUS_SOAP_NOTE_SOCIAL_UPDATE_LABEL)) {
    return { label: null, text: preview };
  }

  return {
    label: PREVIOUS_SOAP_NOTE_SOCIAL_UPDATE_LABEL.trimEnd(),
    text: preview.slice(PREVIOUS_SOAP_NOTE_SOCIAL_UPDATE_LABEL.length),
  };
}

const SIGNED_AT_LABELS: Record<AppointmentPreviousSoapNoteSignedAtBasis, string> = {
  signed: "Signed",
  completed: "Completed in SimplePractice",
  created: "Created in SimplePractice",
};

/** How a note's date was established; an absent basis reads as signed. */
export function signedAtLabel(
  basis?: AppointmentPreviousSoapNoteSignedAtBasis
): string {
  return SIGNED_AT_LABELS[basis ?? "signed"];
}

/** True exactly for a SimplePractice import (completed or created basis). */
export function isSimplePracticeImport(
  basis?: AppointmentPreviousSoapNoteSignedAtBasis
): boolean {
  return basis === "completed" || basis === "created";
}

/** The overview preview slot for an import, which has no derived preview. */
export const SIMPLEPRACTICE_IMPORT_LINE =
  "Imported from SimplePractice. Open it to read the full note.";

/** "Aug 9, 2026" in the clinic's timezone. */
export function formatNoteDate(value: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  }).format(new Date(value));
}
